import { useRef } from 'react';
import type { WrapperFC } from '@george-labs.com/design-system';
import { OverviewCardMenu } from '@george-labs.com/design-system';

import type { Account } from '@shared/products-types';
import { useNotifications } from '@shared/snackbar';
import type { ProductWithHelpers } from '@shared/products-core';
import { useActiveCustomizationFeature } from '@shared/products-core';
import type { CopyProductNumberAction } from '@retail/overview-utils';

import { ProductImageUpload } from '../../personalization/ProductImageUpload';
import type { DropdownEntry } from '../../overview-actions-dropdown/types';
import { CardMenuDropdownToggle } from '../../overview-actions-dropdown/CardMenuDropdownToggle';
import { useDropdownEntries } from '../../overview-actions-dropdown/useDropdownEntries';
import {
    getDropdownActions,
    getRegisteredDropdownActions,
} from '../../overview-actions-dropdown/ActionsDropdownService';
import { ColorChangeDropdown } from './ColorChangeDropdown';
import { NameChangeDropdown } from './NameChangeDropdown';
import { useAliasSaveFocusRestoration } from './useAliasSaveFocusRestoration';

interface Props {
    useRegisteredItems?: boolean;
    isAliasChangeAllowed?: boolean;
    copyProductNumberAction?: CopyProductNumberAction;
    settingsUrl?: string;
    account: ProductWithHelpers<Account>;
}

export const ActionsDropdown: WrapperFC<Props> = ({
    useRegisteredItems = false,
    isAliasChangeAllowed = true,
    copyProductNumberAction,
    settingsUrl,
    account,
}) => {
    const { displaySuccess } = useNotifications('overview-dropdown-actions');
    const {
        isAliasChangeCustomizationActive,
        isColorChangeCustomizationActive,
        activateAliasChangeCustomization,
        activateColorChangeCustomization,
    } = useActiveCustomizationFeature();
    const imageUploadInputRef = useRef<HTMLInputElement>(null);
    const { handleAliasChangeSaved } = useAliasSaveFocusRestoration({
        isAliasChangeCustomizationActive,
        overviewCardDataCy: `overview-card-${account.id}`,
    });

    const dropdownActions = useRegisteredItems
        ? getRegisteredDropdownActions(
              account,
              isAliasChangeAllowed,
              copyProductNumberAction,
              settingsUrl,
          )
        : getDropdownActions(account, isAliasChangeAllowed);

    const { dropdownEntries, performEntryAction } = useDropdownEntries(
        dropdownActions,
        displaySuccess,
    );
    if (!dropdownEntries.length) {
        return null;
    }

    if (isColorChangeCustomizationActive) {
        return <ColorChangeDropdown account={account} />;
    }

    if (isAliasChangeCustomizationActive) {
        return <NameChangeDropdown onSave={handleAliasChangeSaved} account={account} />;
    }

    const onSelect = (action: DropdownEntry) => {
        if (action.value === 'CHANGE_IMAGE') {
            imageUploadInputRef.current?.click();
            return;
        }

        if (action.value === 'CHANGE_COLOR') {
            activateColorChangeCustomization();
            return;
        }

        if (action.value === 'CHANGE_NAME') {
            activateAliasChangeCustomization();
            return;
        }

        performEntryAction(action.value);
    };

    const renderToggle = () => (
        <CardMenuDropdownToggle data-cy="overview-card-secondary-menu-button" />
    );

    return (
        <>
            <OverviewCardMenu items={dropdownEntries} onSelect={onSelect} toggle={renderToggle} />
            <ProductImageUpload
                ref={imageUploadInputRef}
                productId={account.id}
                productI18N={account.productI18N}
                productType={account.type}
                hasImage={account.hasImage ?? false}
                imageChangeContext="overview"
                trigger="overview"
            />
        </>
    );
};

ActionsDropdown.wrapsComponent = OverviewCardMenu;
